export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

// Super-admin "view as org" scope. When set, every API call carries
// `X-Organization-Id: <id>` so the backend treats the request as if the
// super admin were a member of that org. `null` = the super admin's own
// org (default). Persisted in sessionStorage so a hard refresh keeps the
// view; React calls setApiScopeOrg() to mutate it.
let apiScopeOrg: string | null = null;
try {
  apiScopeOrg = sessionStorage.getItem('rabitni.scope_org_id');
} catch {
  /* SSR / disabled storage */
}

export function getApiScopeOrg(): string | null {
  return apiScopeOrg;
}

export function setApiScopeOrg(orgId: string | null) {
  apiScopeOrg = orgId;
  try {
    if (orgId) sessionStorage.setItem('rabitni.scope_org_id', orgId);
    else sessionStorage.removeItem('rabitni.scope_org_id');
  } catch {
    /* ignore */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (apiScopeOrg) headers['X-Organization-Id'] = apiScopeOrg;

  const res = await fetch(path, {
    credentials: 'include',
    headers,
    ...init,
  });

  const text = await res.text();
  const body = text ? safeParseJson(text) : null;

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (body && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      if (typeof b.message === 'string' && b.message) message = b.message;
      else if (typeof b.error === 'string' && b.error) message = b.error;
    }
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(p: string) => request<T>(p, { method: 'GET' }),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
};
